import type { FlightCardData } from '@massilia/queries'

import { Button, Checkbox, cn, Label } from '@massilia/ui'
import { ChevronDown } from 'lucide-react'
import { useMemo, useState } from 'react'

import { FilterDrawer } from './filter-drawer'
import { getFilterAirline } from './filter-utils'
import { useFiltersState } from './filters-state-context'
import { FlightCountBadge } from './flight-count-badge'

export interface AirlineOption {
  name: string
  code: string
}

interface CompanyFilterProps {
  airlines: AirlineOption[]
  offers: FlightCardData[]
}

const MAX_AIRLINES_SHOWN = 5

export function CompanyFilter({ airlines, offers }: CompanyFilterProps) {
  const {
    filters: { filterAirlines },
    setFilters,
  } = useFiltersState()

  const excludedCodes = useMemo(() => {
    return filterAirlines ? filterAirlines.split(',') : []
  }, [filterAirlines])

  const [showAll, setShowAll] = useState(false)
  const [hoveredCode, setHoveredCode] = useState<null | string>(null)

  // Calculate flight counts per airline
  const airlineCounts = useMemo(() => {
    const counts: Record<string, number> = {}

    for (const airline of airlines) {
      counts[airline.code] = 0
    }
    for (const offer of offers) {
      // Même axe que la liste des compagnies (vendeuse) : sinon toute offre
      // intermodale compterait pour AccesRail, absent de la liste.
      const { code } = getFilterAirline(offer)
      counts[code] = (counts[code] || 0) + 1
    }

    return counts
  }, [airlines, offers])

  const displayedAirlines = showAll ? airlines : airlines.slice(0, MAX_AIRLINES_SHOWN)

  const handleCheckboxChange = (code: string, checked: boolean) => {
    if (checked) {
      setFilters({ filterAirlines: excludedCodes.filter((c) => c !== code).join(',') })
    } else {
      setFilters({ filterAirlines: [...excludedCodes, code].join(',') })
    }
  }

  const handleOnlyThis = (code: string) => {
    // Exclure toutes les compagnies SAUF celle-ci
    const allOtherCodes = airlines.filter((a) => a.code !== code).map((a) => a.code)
    setFilters({ filterAirlines: allOtherCodes.join(',') })
  }

  const handleReset = () => {
    setFilters({ filterAirlines: undefined })
  }

  return (
    <FilterDrawer onReset={handleReset} showReset={excludedCodes.length > 0} title="Compagnies">
      <div className="flex flex-col gap-2">
        {/* Individual airlines */}
        {displayedAirlines.map((airline) => {
          const count = airlineCounts[airline.code] || 0
          const isDisabled = count === 0
          const isVisible = !excludedCodes.includes(airline.code)
          const isHovered = hoveredCode === airline.code

          return (
            <div
              className="group relative -mx-1 flex items-center justify-between rounded px-1 transition-colors hover:bg-gray-50"
              key={airline.code}
              onMouseEnter={() => {
                setHoveredCode(airline.code)
              }}
              onMouseLeave={() => {
                setHoveredCode(null)
              }}
            >
              <Label
                className="flex flex-1 cursor-pointer items-center gap-2"
                htmlFor={`airline-${airline.code}`}
              >
                <Checkbox
                  checked={isVisible}
                  disabled={isDisabled}
                  id={`airline-${airline.code}`}
                  onCheckedChange={(checked) => {
                    handleCheckboxChange(airline.code, checked === true)
                  }}
                />
                <span
                  className={cn(
                    'text-xs',
                    { 'text-gray-400': isDisabled },
                    { 'text-gray-700': !isDisabled }
                  )}
                >
                  {airline.name}
                </span>
              </Label>

              <div className="flex items-center gap-2">
                {/* Bouton "Uniquement" visible au hover */}
                {isHovered && !isDisabled && (
                  <button
                    className="text-xs font-medium text-brand-600 hover:text-brand-800 hover:underline"
                    onClick={(e) => {
                      e.preventDefault()
                      handleOnlyThis(airline.code)
                    }}
                    type="button"
                  >
                    Uniquement
                  </button>
                )}
                <FlightCountBadge count={count} disabled={isDisabled} />
              </div>
            </div>
          )
        })}

        {airlines.length > MAX_AIRLINES_SHOWN && (
          <Button
            className="mt-2 flex items-center text-sm text-brand-600 hover:text-brand-800"
            onClick={() => {
              setShowAll(!showAll)
            }}
            variant="outline"
          >
            {showAll ? 'Voir moins' : 'Voir plus'}
            <span
              className={cn('ml-1 transform transition-transform duration-200', {
                'rotate-180': showAll,
              })}
            >
              <ChevronDown />
            </span>
          </Button>
        )}

        {airlines.length === 0 && (
          <p className="text-sm text-gray-500">Aucune compagnie disponible</p>
        )}
      </div>
    </FilterDrawer>
  )
}
